import React, { useState, useEffect } from "react";
import { FaCloudSun, FaCloud, FaCloudRain, FaSnowflake, FaBolt, FaSmog, FaTemperatureHigh, FaWind, FaSun } from "react-icons/fa";

const getWeatherIcon = (forecast) => { 
  if (!forecast) return <FaCloud />;
  const f = forecast.toLowerCase();
  if (f.includes("sun") || f.includes("clear")) return <FaSun />;
  if (f.includes("cloud")) return <FaCloud />;
  if (f.includes("rain") || f.includes("shower")) return <FaCloudRain />;
  if (f.includes("snow")) return <FaSnowflake />;
  if (f.includes("thunder")) return <FaBolt />;
  if (f.includes("fog")) return <FaSmog />;
  return <FaTemperatureHigh />;
}
// Props: latitude, longitude, locationName (optional)
const WeatherConditions = ({ latitude, longitude, locationName }) => {
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [weatherData, setWeatherData] = useState(null);

  useEffect(() => {
    if (!latitude || !longitude) return;
    const fetchWeather = async () => {
      setWeatherLoading(true);
      setWeatherError("");
      setWeatherData(null);
      try {
        const WEATHER_API_BASE = process.env.REACT_APP_WEATHER_API || "https://api.weather.gov";
        // Step 1: Get gridpoint with timeout
        const pointsRes = await Promise.race([
          fetch(`${WEATHER_API_BASE}/points/${latitude},${longitude}`),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Weather grid fetch timeout")), 10000)
          )
        ]);
        if (!pointsRes.ok) {
          setWeatherError(`Failed to get weather grid: ${pointsRes.status}`);
          setWeatherLoading(false);
          return;
        }
        const pointsJson = await pointsRes.json();
        const forecastUrl = pointsJson?.properties?.forecast;
        if (!forecastUrl) {
          setWeatherError("No forecast URL found for this location.");
          setWeatherLoading(false);
          return;
        }
        // Step 2: Get forecast with timeout
        const forecastRes = await Promise.race([
          fetch(forecastUrl),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Forecast fetch timeout")), 15000)
          )
        ]);
        if (!forecastRes.ok) {
          setWeatherError(`Failed to get weather forecast: ${forecastRes.status}`);
          setWeatherLoading(false);
          return;
        }
        const forecastJson = await forecastRes.json();
        setWeatherData(forecastJson);
      } catch (err) {
        setWeatherError("Failed to fetch weather data: " + (err.message || "Unknown error"));
      } finally {
        setWeatherLoading(false);
      }
    };
    fetchWeather();
  }, [latitude, longitude]);

  return (
    <div style={{ background: "#e0f2fe", borderRadius: 8, padding: 12, marginTop: 8 }}>
      {weatherLoading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="animate-spin" style={{ width: 16, height: 16, border: "2px solid #3b82f6", borderTop: "2px solid transparent", borderRadius: "50%", display: "inline-block" }}></span>
          <span>Loading weather...</span>
        </div>
      ) : weatherError ? (
        <div style={{ color: "#ef4444" }}>{weatherError}</div>
      ) : weatherData && weatherData.properties && weatherData.properties.periods && weatherData.properties.periods.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {weatherData.properties.periods.slice(0, 6).map((period, idx) => (
            <div key={period.number || idx} style={{ background: "#fff", borderRadius: 6, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: 8, display: "flex", flexDirection: "column", gap: 4, border: "1px solid #bae6fd" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 500 }}>{period.name}</span>
                <span style={{ fontSize: 12, color: "#64748b" }}>{period.startTime ? new Date(period.startTime).toLocaleDateString() : ""}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>{getWeatherIcon(period.shortForecast)}</span>
                <span>{period.shortForecast}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <span><FaTemperatureHigh /> {period.temperature}°{period.temperatureUnit}</span>
                <span><FaWind /> {period.windSpeed} {period.windDirection}</span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{period.detailedForecast}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: "#64748b" }}>No weather data available.</div>
      )}
    </div>
  );
};

export default WeatherConditions;
